import axios from 'axios';

// Get the OpenAI API key from environment variables
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

/**
 * Cleans markdown from OpenAI responses, particularly handling code blocks
 * @param {string} text - The response text from OpenAI
 * @returns {string} - Cleaned text
 */
const cleanMarkdown = (text) => {
  // Remove markdown code block indicators
  text = text.replace(/```json\s*/g, '');
  text = text.replace(/```\s*$/g, '');
  text = text.replace(/```/g, '');
  return text.trim();
};

/**
 * Analyzes a biotech product's development path using OpenAI
 * @param {Object} productData - The product data from the database
 * @param {string} targetMarket - The target market for analysis (e.g., 'Global', 'US', 'EU')
 * @param {string} competitorNotes - Optional notes about competitors
 * @returns {Promise<Object>} - The analysis results
 */
export const analyzeProductDevelopmentPath = async (productData, targetMarket, competitorNotes = '') => {
  // Check if API key is configured
  if (!OPENAI_API_KEY) {
    throw new Error('OpenAI API key is not configured. Please add it to your .env.local file.');
  }

  try {
    // Create a prompt for the analysis
    const prompt = `
    I need a detailed development path analysis for a biotech product with the following information:
    
    Product Name: ${productData.name}
    Development Stage: ${productData.development_stage}
    Product Category: ${productData.product_category || 'Not specified'}
    Concept Description: ${productData.concept_description || 'Not specified'}
    Concept Validation Method: ${productData.concept_validation_method || 'Not specified'}
    Indications of Use: ${productData.indications_of_use || 'Not specified'}
    Target Market: ${targetMarket}
    ${competitorNotes ? `Competitor Notes: ${competitorNotes}` : ''}
    
    Please provide a detailed analysis with the following information:
    1. Estimated market size in USD for the target market (${targetMarket})
    2. Potential competitor products in the market
    3. A step-by-step development path from current stage to market, including for each step:
       - Step name
       - Estimated duration
       - Estimated cost range
       - Success probability
    4. Total estimated development time (years)
    5. Total estimated development cost (USD)
    6. Overall success probability (%)
    7. Strategic recommendations for development
    
    Return ONLY valid JSON with no explanation or markdown formatting. The response should be a JSON object with the following structure:
    {
      "market_size": "string (e.g., $X billion USD)",
      "competitor_products": "string with competitor descriptions",
      "development_steps": [
        {
          "step": "string - step name",
          "duration": "string - timeframe",
          "cost": "string - cost range",
          "success_probability": "string - percentage"
        }
      ],
      "total_development_time": "string - timeframe",
      "total_development_cost": "string - cost range",
      "overall_success_probability": "string - percentage",
      "recommendations": "string - strategic recommendations"
    }
    `;

    // Make API call to OpenAI
    const response = await axios.post(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'gpt-4-turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a biotech development and financial expert specializing in drug development pathways, market analysis, and financial forecasting. Provide realistic estimates based on industry standards and current market conditions. Return ONLY valid JSON with no markdown formatting.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.5,
        max_tokens: 2000
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        }
      }
    );

    // Parse the response
    const responseData = response.data.choices[0].message.content;
    console.log("Raw OpenAI response:", responseData);
    
    try {
      // Clean markdown and try to parse
      const cleanedResponse = cleanMarkdown(responseData);
      console.log("Cleaned response:", cleanedResponse);
      
      // Parse the JSON response
      const analysisData = JSON.parse(cleanedResponse);
      return analysisData;
    } catch (parseError) {
      console.error('Error parsing OpenAI response:', parseError);
      
      // If JSON parsing fails, try to extract the JSON portion
      const jsonMatch = responseData.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          return JSON.parse(jsonMatch[0]);
        } catch (innerError) {
          console.error('Error parsing extracted JSON:', innerError);
          throw new Error('Failed to parse OpenAI response');
        }
      }
      
      throw new Error('Failed to parse OpenAI response: ' + parseError.message);
    }
  } catch (error) {
    console.error('OpenAI API error:', error);
    
    // Show specific error message based on error type
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('OpenAI API error response:', error.response.data);
      throw new Error(`OpenAI API error: ${error.response.data.error?.message || error.response.statusText}`);
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error('No response received from OpenAI API');
    } else {
      // Something happened in setting up the request that triggered an Error
      throw error;
    }
  }
};

/**
 * Generates a financial forecast for a biotech product using OpenAI
 * @param {Object} productData - The product data
 * @param {Object} financialParams - Financial parameters like discount rate, etc.
 * @returns {Promise<Object>} - The financial forecast results
 */
export const generateFinancialForecast = async (productData, financialParams) => {
  // Check if API key is configured
  if (!OPENAI_API_KEY) {
    throw new Error('OpenAI API key is not configured. Please add it to your .env.local file.');
  }

  try {
    // Create a prompt for the financial analysis
    const prompt = `
    I need a detailed financial forecast for a biotech product with the following information:
    
    Product Name: ${productData.name}
    Development Stage: ${productData.development_stage}
    Product Category: ${productData.product_category || 'Not specified'}
    Overall Project Risk: ${financialParams.overallProjectRisk}
    Discount Rate: ${financialParams.discountRate}%
    Duration of Exclusivity: ${financialParams.durationOfExclusivity} years
    Gross Profit Margin: ${financialParams.grossProfitMargin}%
    R&D Costs to Preclinical POC: ${financialParams.rdCostsToPreclinicalPoc || 'Not specified'}
    
    Please provide a detailed financial analysis with:
    1. NPV (Net Present Value)
    2. IRR (Internal Rate of Return)
    3. Year-by-year revenue projections (10 years)
    4. Break-even analysis
    5. Key financial metrics
    6. Sensitivity analysis
    7. Financial recommendations
    
    Return a JSON object with the following structure (ensure these exact field names):
    {
      "npv": "value with $ symbol",
      "irr": "percentage",
      "revenueProjections": { "Year 1": value, "Year 2": value, ... },
      "breakEvenPoint": "Year X",
      "keyMetrics": { "metric1": "value", "metric2": "value", ... },
      "sensitivityAnalysis": "text analysis",
      "recommendations": "strategic recommendations"
    }
    
    Return ONLY valid JSON with no explanation or markdown formatting.
    `;

    // Make API call to OpenAI
    const response = await axios.post(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'gpt-4-turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a biotech financial expert specializing in pharmaceutical and medical device financial analysis and forecasting. Provide realistic financial projections based on industry standards. Return ONLY valid JSON with no markdown formatting. Use the exact field names specified in the request.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.5,
        max_tokens: 2000
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        }
      }
    );

    // Parse the response
    const responseData = response.data.choices[0].message.content;
    console.log("Raw OpenAI financial response:", responseData);
    
    try {
      // Clean markdown and try to parse
      const cleanedResponse = cleanMarkdown(responseData);
      console.log("Cleaned financial response:", cleanedResponse);
      
      // Parse the JSON response
      let financialData = JSON.parse(cleanedResponse);
      
      // Check if we have a nested "Financial Analysis" structure and flatten it
      if (financialData["Financial Analysis"]) {
        const nestedData = financialData["Financial Analysis"];
        
        // Create a new standardized object
        financialData = {
          // Extract data or provide defaults for all expected fields
          npv: nestedData.NPV || '$0',
          irr: nestedData.IRR || '0%',
          revenueProjections: nestedData["Year-by-year Revenue Projections"] || {},
          breakEvenPoint: nestedData["Break-even Analysis"]?.["Break-even Year"] || 'N/A',
          keyMetrics: nestedData["Key Financial Metrics"] || {},
          sensitivityAnalysis: nestedData["Sensitivity Analysis"] 
            ? (typeof nestedData["Sensitivity Analysis"] === 'string' 
              ? nestedData["Sensitivity Analysis"] 
              : JSON.stringify(nestedData["Sensitivity Analysis"], null, 2))
            : 'Not available',
          recommendations: nestedData["Financial Recommendations"] || 'No recommendations available'
        };
      }
      
      // Keep the original response for debugging
      financialData._originalResponse = cleanedResponse;
      
      return financialData;
    } catch (parseError) {
      console.error('Error parsing OpenAI response:', parseError);
      
      // If JSON parsing fails, try to extract the JSON portion
      const jsonMatch = responseData.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          return JSON.parse(jsonMatch[0]);
        } catch (innerError) {
          console.error('Error parsing extracted JSON:', innerError);
          throw new Error('Failed to parse OpenAI response');
        }
      }
      
      throw new Error('Failed to parse OpenAI response: ' + parseError.message);
    }
  } catch (error) {
    console.error('OpenAI API error:', error);
    throw error;
  }
};

/**
 * Generates pitch deck slides based on product data and development stage
 * @param {Object} productData - The product data
 * @param {Object} additionalData - Additional data including team members, assets, etc.
 * @returns {Promise<Object>} - The generated pitch deck slides
 */
export const generatePitchDeck = async (productData, additionalData) => {
  if (!OPENAI_API_KEY) {
    throw new Error('OpenAI API key is not configured. Please add it to your .env.local file.');
  }

  try {
    console.log('Generating pitch deck for product:', productData.name);
    
    const { teamMembers, assets, funding, patents, technologies, productAnalysis, financialAnalysis } = additionalData;
    
    // Create prompt based on development stage
    let slidesPrompt = "";
    const stage = productData.development_stage || 'Discovery';
    
    if (stage === 'Discovery') {
      slidesPrompt = `
        Generate a professional pitch deck for a biotechnology product in the Discovery stage with the following slides:
        1. Cover slide - Include product name, company, and a tagline
        2. Market Needs and Opportunity Slide - Describe the market problem and size
        3. Product Feature Slide - Key features and benefits
        4. Product Technology Slide - Technical foundation and innovation
        5. Competition Landscape Slide - Key competitors and our advantages
        6. Development Plan and Financial Outlook Slide - Timeline and financial projections
        7. Team and Asset Slide - Key personnel and resources
        8. Funding details slide - Current funding and future needs
      `;
    } else {
      // For future stages (to be implemented later)
      slidesPrompt = `
        Generate a professional pitch deck for a biotechnology product in the ${stage} stage with appropriate slides.
      `;
    }
    
    const messages = [
      {
        role: "system",
        content: `You are an expert in biotechnology investment and pitch deck creation. 
        Your task is to create compelling, informative pitch deck slides for a biotechnology product.
        Format your response as a JSON object with keys for each slide title and values containing the slide content.
        Include a suggested slide_order array with the recommended sequence of slides.`
      },
      {
        role: "user",
        content: `
        Create pitch deck slides for the following biotechnology product:
        
        Product Name: ${productData.name}
        Product Description: ${productData.concept_description}
        Indications: ${productData.indications_of_use}
        Development Stage: ${stage}
        
        ${productAnalysis ? `Market Size Data: ${JSON.stringify(productAnalysis.market_size || {})}` : ''}
        ${productAnalysis ? `Competition Data: ${JSON.stringify(productAnalysis.competition || {})}` : ''}
        ${productAnalysis ? `Development Timeline: ${JSON.stringify(productAnalysis.development_timeline || {})}` : ''}
        
        ${financialAnalysis ? `Financial Projections: ${JSON.stringify(financialAnalysis.projections || {})}` : ''}
        ${financialAnalysis ? `Financial Metrics: ${JSON.stringify(financialAnalysis.metrics || {})}` : ''}
        
        Team Members: ${JSON.stringify(teamMembers || [])}
        Assets: ${JSON.stringify(assets || [])}
        Funding: ${JSON.stringify(funding || [])}
        Patents: ${JSON.stringify(patents || [])}
        Technologies: ${JSON.stringify(technologies || [])}
        
        ${slidesPrompt}
        
        Make the content compelling and investor-focused. Use specific details from the provided data.
        If certain information is missing, create reasonable content based on the product's stage and indications.
        `
      }
    ];

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-4-turbo-preview',
        messages: messages,
        temperature: 0.7,
        max_tokens: 3000
      })
    });

    const data = await response.json();
    
    if (data.error) {
      console.error('OpenAI API error:', data.error);
      throw new Error(`OpenAI API error: ${data.error.message}`);
    }

    let pitchDeckContent;
    try {
      const cleanedText = cleanMarkdown(data.choices[0].message.content);
      console.log('Raw pitch deck response:', cleanedText);
      pitchDeckContent = JSON.parse(cleanedText);
    } catch (err) {
      console.error('Error parsing JSON from OpenAI response:', err);
      console.log('Raw response text:', data.choices[0].message.content);
      throw new Error('Failed to parse pitch deck data from OpenAI. Please try again.');
    }

    return pitchDeckContent;
  } catch (error) {
    console.error('Error in generatePitchDeck:', error);
    
    // For testing/fallback, return mock data if OpenAI call fails
    return {
      "Cover": {
        "title": productData.name,
        "subtitle": "Innovative Biotechnology Solution",
        "tagline": "Transforming Patient Care in " + (productData.indications_of_use || "Healthcare")
      },
      "Market_Needs": {
        "problem": "Addressing significant unmet medical needs",
        "market_size": "$5-10 billion global market opportunity",
        "growth": "Expected 15% CAGR over the next decade"
      },
      "Product_Features": {
        "key_features": ["Targeted therapy", "Reduced side effects", "Cost-effective treatment"],
        "benefits": ["Improved patient outcomes", "Shorter hospital stays", "Greater quality of life"]
      },
      "Technology": {
        "platform": "Proprietary biotechnology platform",
        "innovation": "Novel mechanism of action",
        "ip_protection": "Strong patent position"
      },
      "Competition": {
        "main_competitors": ["Competitor A", "Competitor B"],
        "advantages": ["Superior efficacy", "Better safety profile", "Lower production costs"]
      },
      "Development_Plan": {
        "timeline": "36-month development timeline",
        "milestones": ["Preclinical completion: Q4 2023", "IND filing: Q2 2024", "Phase 1 trials: Q4 2024"],
        "financials": "Break-even projected by year 5"
      },
      "Team": {
        "leadership": "Experienced executive team with 50+ years combined biotech experience",
        "advisors": "World-class scientific advisory board",
        "key_assets": "State-of-the-art R&D facilities"
      },
      "Funding": {
        "raised": "$2.5M seed funding to date",
        "seeking": "Series A: $15M",
        "use_of_funds": ["Complete preclinical studies", "Prepare IND filing", "Build out clinical team"]
      },
      "slide_order": [
        "Cover", 
        "Market_Needs", 
        "Product_Features", 
        "Technology", 
        "Competition", 
        "Development_Plan", 
        "Team", 
        "Funding"
      ]
    };
  }
};

// Create a named export object
const openaiService = {
  analyzeProductDevelopmentPath,
  generateFinancialForecast,
  generatePitchDeck
};

export default openaiService; 